<template>
  <default-textual-section v-if="$store.state.displayType === 'textual'" :menu="menu" :section="section"
                           :menu-item-component="menuItemComponent"
                           class="menu--section-christmas_libations">
    <template v-slot:beforeTopContentArea>
      <img class="menu--section_holiday_divider" src="/library/images/menu/christmas-libations-divider.png"
           alt="Holiday Divider" aria-hidden="true" />
    </template>
  </default-textual-section>
  <default-visual-section v-else :menu="menu" :section="section"
                          :menu-item-component="menuItemComponent"
                          class="menu--section-christmas_libations">
    <template v-slot:beforeTopContentArea>
      <img class="menu--section_holiday_divider" src="/library/images/menu/christmas-libations-divider.png"
           alt="Holiday branch" aria-hidden="true" />
    </template>
  </default-visual-section>
</template>

<script lang="ts">
/**
 * Custom component for Christmas Libations default menu sections.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 0.1.0
 * @copyright  Copyright (C) 2025-2026, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *  2025-11-05 (1.0.0) (jhoward)
 *    - Copy from ChristmasLibations_defaultSection.vue component to ChristmasLibations2025_defaultSection.vue component.
 *    - Update divider image in ChristmasLibations2025_defaultSection to the 2025 version.
 *    - Update background images in ChristmasLibations2025_defaultSection to the 2025 versions and refactor to support more device types.
 * @changelog
 *
 * 2026-07-20 (0.1.0) (jhoward)
 *   - Add App\Library\Js\Components\Menu\Section\ChristmasLibations2025_defaultSection.vue for Christmas Libations 2025 menu sections.
 */

import {defineComponent, PropType} from "vue";
import {ALLOWED_ITEM_COMPONENTS, Menu, MenuSection} from "../defs";
import DefaultVisualSection from "./visual/defaultVisualSection.vue";
import DefaultTextualSection from "./textual/defaultTextualSection.vue";

export default defineComponent({
  components: {
    DefaultTextualSection, DefaultVisualSection,
  },
  name: "ChristmasLibations_defaultSection",
  props: {
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    menu: {
      type: Object as PropType<Menu>,
      required: true
    },
    menuItemComponent: {
      type: String,
      default: 'christmas-drink-item',
      validate: (value: string) => '' === value || ALLOWED_ITEM_COMPONENTS.indexOf(value)
    },
  },
})
</script>

<style>
html .search--container + .visual_menu--page {
  padding-top: 0;
}
</style>

<style scoped>
.menu--section-christmas_libations {
  font-size: 1.3em;
  color: #231f20;
  padding: 6em 1em 15rem;
  font-family: bodoni-urw, serif;
  min-height: 100vh;

  background-image: url('/library/images/menu/background-middle-christmas-libations-wv-2025.jpg');
  background-repeat: repeat-y;
  background-size: 100% auto;
  background-position: center top;
}

.menu_switcher--container + .visual_menu--page .menu--section-christmas_libations:first-child {
  padding-top: 10rem;
}

.menu--section-christmas_libations::before,
.menu--section-christmas_libations::after {
  content: '';
  position: absolute;
  z-index: -1;
  top: 0;
  left: 0;
  width: 100%;
  height: 402px;
  background-image: url('/library/images/menu/background-top-christmas-libations-2025.jpg');
  background-size: 100% auto;
  background-repeat: no-repeat;
  background-position: top center;
}

.menu--section-christmas_libations::after {
  top: auto;
  bottom: 0;
  height: 191px;
  background-image: url('/library/images/menu/background-bottom-christmas-libations-reno-2025.jpg');
  background-position: bottom center;
}

.visual_menu--container-the_steak_house .menu--section-christmas_libations::after,
.visual_menu--container-pancho__willies .menu--section-christmas_libations::after {
  background-image: url('/library/images/menu/background-bottom-christmas-libations-wv-2025.jpg');
}

/*@media (max-width: 500px) {
  .menu--section-christmas_libations::before {
    background-image: initial !important;
  }
}*/

.menu--section-christmas_libations:deep(.visual_menu--highlight-christmas_libations) {
  font-weight: bold;
  color: #e21f26;
}

.menu--section-christmas_libations:deep(.menu--item_title-christmas_libations) {
  font-size: 1.2em;
  letter-spacing: 0.1em;
  color: #e21f26;
}

.menu--section-christmas_libations:deep(h2) {
  padding-left: 0;
  padding-right: 0;
}

.menu--section-christmas_libations:deep(h2 img) {
  width: 65%;
}

.menu--section-christmas_libations:deep(.menu--section_title) {
  color: #e21f26;
  font-size: 2em !important;
  position: relative;
  overflow: initial;
  font-family: bodoni-urw, serif;
  padding-bottom: 0;
  margin-top: 20%;
}

@media (max-width: 600px) {
  .menu--section-christmas_libations:deep(.menu--section_title) {
    margin-top: 25%;
  }
}

@media (max-width: 300px) {
  .menu--section-christmas_libations:deep(.menu--section_title) {
    margin-top: 0;
  }
}

.menu--section-christmas_libations:deep(.menu--section_subtitle) {
  text-align: center;
  font-family: itc-avant-garde-gothic-pro, sans-serif;
  color: #000;
  font-size: 1em;
  line-height: 1.4em;
  margin-top: 0.5em;
  margin-bottom: 0.5em;
}

.menu--section-christmas_libations:deep(.visual_menu--page_section.visual_menu--page_section-top) {
  position: relative;
  font-size: 0.8em;
  font-weight: lighter;
  padding-bottom: 2.1em;
}

.menu--section-christmas_libations:deep(.visual_menu--page_section.visual_menu--page_section-top p) {
  margin-top: 0;
  margin-bottom: 0;
}

.menu--section-christmas_libations:deep(.visual_menu--page_section-bottom .visual_menu--page_section-bottom) {
  padding-top: 1.25em;
  position: relative;
  font-size: 0.9em;
  font-weight: lighter;
  padding-left: 0;
  padding-right: 0;
}

.menu--section-christmas_libations:deep(.menu--section_holiday_divider) {
  margin: 2em auto 2.4em auto;
  display: block;
  max-width: 14%;
}

.menu--section-christmas_libations:deep(.menu--item_description .menu--item_title-christmas_libations ~ .menu--item_title-christmas_libations) {
  font-size: 0.83em;
}

.menu--section-christmas_libations:deep(.menu--item + .menu--item) {
  margin-top: 4em;
}

.menu--section-christmas_libations:deep(.menu--item + .menu--item::before) {
  content: '';
  position: absolute;
  top: -3em;
  left: 50%;
  width: 90px;
  height: 18px;
  margin: -9px 0 0 -45px;
  background-image: url('/library/images/menu/christmas-libations-divider.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: 50% 50%;
}

.menu--section-christmas_libations.textual_menu--section.body-container:deep(.viewport) {
  width: 70%;
}

.menu--section-christmas_libations:deep(.menu--item_group) {
  width: 70%;
  margin: 0 auto;
}

@media (max-width: 600px) {
  .menu--section-christmas_libations.textual_menu--section.body-container:deep(.viewport),
  .menu--section-christmas_libations:deep(.menu--item_group) {
    width: 100%;
  }
}
</style>